import { useState, useEffect } from 'react';
import { Header } from './components/Header';
import { Vortex } from './components/Vortex';
import { VerticalSlider } from './components/VerticalSlider';
import { DimensionTabs } from './components/DimensionTabs';
import { ControlPanel } from './components/ControlPanel';
import { AudioPlayer } from './components/AudioPlayer';
import { LoginForm } from './components/LoginForm';
import { SignupForm } from './components/SignupForm';
import { LoraManager } from './components/LoraManager';
import { useAuth } from './hooks/useAuth';
import { api } from './services/api';
import { loraApi } from './services/loraApi';
import type { Dimension, LoraModel } from './types';
import './App.css';

function App() {
  const { user, isAuthenticated, isLoading, logout, getAccessToken } = useAuth();
  const [authMode, setAuthMode] = useState<'login' | 'signup'>('login');
  const [dimension, setDimension] = useState<Dimension>('generate');
  const [isGenerating, setIsGenerating] = useState(false);
  const [audioBase64, setAudioBase64] = useState<string | null>(null);
  const [sampleRate, setSampleRate] = useState<number | null>(null);
  const [_lastSeed, setLastSeed] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sourceAudio, setSourceAudio] = useState<string | null>(null);

  // LoRA model selection
  const [selectedModelId, setSelectedModelId] = useState<string | null>(null);
  const [selectedModelName, setSelectedModelName] = useState<string>('ACE-Step (Base)');
  const [isLoraManagerOpen, setIsLoraManagerOpen] = useState(false);

  // Slider values for visual effect
  const [intensity, setIntensity] = useState(50);
  const [depth, setDepth] = useState(50);

  // Connect auth to API service
  useEffect(() => {
    api.setAuthTokenGetter(getAccessToken);
    loraApi.setAuthTokenGetter(getAccessToken);
  }, [getAccessToken]);

  // Fetch models to get selected model name
  useEffect(() => {
    const fetchModelName = async () => {
      if (selectedModelId) {
        try {
          const response = await loraApi.list();
          const model = response.models.find((m: LoraModel) => m.id === selectedModelId);
          if (model) {
            setSelectedModelName(model.name);
          }
        } catch {
          // Silently fail
        }
      } else {
        setSelectedModelName('ACE-Step (Base)');
      }
    };
    fetchModelName();
  }, [selectedModelId]);

  // Show loading while checking auth
  if (isLoading) {
    return (
      <div className="app-loading">
        <div className="loading-spinner" />
      </div>
    );
  }

  // Show login/signup if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="auth-container">
        {authMode === 'login' ? (
          <LoginForm onSwitchToSignup={() => setAuthMode('signup')} />
        ) : (
          <SignupForm onSwitchToLogin={() => setAuthMode('login')} />
        )}
      </div>
    );
  }

  const handleGenerate = async (params: Record<string, unknown>) => {
    setIsGenerating(true);
    setError(null);

    try {
      let result;
      
      switch (dimension) {
        case 'generate':
          result = await api.generate({
            prompt: params.prompt as string,
            lyrics: params.lyrics as string,
            duration: params.duration as number,
            inference_steps: params.inference_steps as number,
            guidance_scale: params.guidance_scale as number,
            seed: params.seed as number,
            model_id: selectedModelId ?? undefined,
          });
          setLastSeed(result.seed);
          break;

        case 'retake':
          result = await api.retake({
            source_audio_b64: params.source_audio_b64 as string,
            prompt: params.prompt as string,
            variance: params.variance as number,
            seed: params.seed as number,
            model_id: selectedModelId ?? undefined,
          });
          if (result.seed) setLastSeed(result.seed);
          break;

        case 'repaint':
          result = await api.repaint({
            source_audio_b64: params.source_audio_b64 as string,
            start_time: params.start_time as number,
            end_time: params.end_time as number,
            prompt: params.prompt as string,
            lyrics: params.lyrics as string,
          });
          break;

        case 'edit':
          result = await api.edit({
            source_audio_b64: params.source_audio_b64 as string,
            new_prompt: params.new_prompt as string,
            new_lyrics: params.new_lyrics as string,
            edit_n_min: params.edit_n_min as number,
            edit_n_max: params.edit_n_max as number,
          });
          break;

        case 'extend':
          result = await api.extend({
            source_audio_b64: params.source_audio_b64 as string,
            left_seconds: params.left_seconds as number,
            right_seconds: params.right_seconds as number,
            prompt: params.prompt as string,
          });
          break;
      }

      if (result) {
        setAudioBase64(result.audio_base64);
        setSampleRate(result.sample_rate);
        // Auto-set as source for subsequent operations
        setSourceAudio(result.audio_base64);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Generation failed');
      console.error('Generation error:', err);
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="app">
      <Header
        selectedPreset={selectedModelName}
        onPresetChange={() => setIsLoraManagerOpen(true)}
        user={user}
        onLogout={logout}
      />

      <LoraManager
        isOpen={isLoraManagerOpen}
        onClose={() => setIsLoraManagerOpen(false)}
        onSelectModel={setSelectedModelId}
        selectedModelId={selectedModelId}
      />

      <main className="app-main">
        <div className="slider-column left">
          <VerticalSlider
            value={intensity}
            min={0}
            max={100}
            topLabel="FORWARD"
            bottomLabel="BACK"
            onChange={setIntensity}
          />
        </div>

        <div className="center-column">
          <div className="vortex-frame">
            <Vortex 
              isActive={isGenerating} 
              intensity={intensity / 50}
            />
            {isGenerating && (
              <div className="generating-overlay">
                <div className="generating-text">
                  <span>GENERATING</span>
                  <div className="dots">
                    <span>.</span><span>.</span><span>.</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          <AudioPlayer 
            audioBase64={audioBase64} 
            sampleRate={sampleRate || undefined}
          />

          {error && (
            <div className="error-banner">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
              </svg>
              {error}
            </div>
          )}

          <DimensionTabs active={dimension} onChange={setDimension} />
        </div>

        <div className="slider-column right">
          <VerticalSlider
            value={depth}
            min={0}
            max={100}
            topLabel="UP"
            bottomLabel="DOWN"
            onChange={setDepth}
          />
        </div>

        <aside className="control-column">
          <ControlPanel
            dimension={dimension}
            onGenerate={handleGenerate}
            isGenerating={isGenerating}
            sourceAudio={sourceAudio}
            onFileUpload={setSourceAudio}
          />
        </aside>
      </main>
    </div>
  );
}

export default App;
